import React from 'react';
import {
    forEach as _forEach,
    get as _get,
    uniq as _uniq,
    reduce as _reduce,
} from 'lodash';
import {useLocation, useParams, useRouteMatch} from 'react-router';
import {Header} from 'semantic-ui-react';

import {
    getCompetitors,
    getContentTierName,
    getInvoiceStatus,
} from '@modules/client/utils/clientBookingTable';
import {dropdownParser} from '@utils/filters';
import {mapPropertyLicencesForDropdown} from '@utils/propertyLicence';
import {sortDropdownOptionsAlphanumerically} from '@utils/sorters';
import responseStatuses from '@constants/responseStatuses';
import {showPopupMessage} from '@utils/popupMessage/PopupMessageService';
import {
    getCategoryLevel,
    getStageEventNameWithTooltip,
    getStagesDescription,
} from '@modules/events/utils/eventSchedule';
import {getLink} from '@appComponents/Link';
import {convertToInt, digitsConnectedByPlus, isUrlParamValid} from '@utils/helpers';
import {routes} from '@constants/routes';
import {createForm, renderModalError} from '@utils/forms';
import {
    showErrorDuringBulkCancel,
    showErrorDuringBulkChange,
    showErrorNoBookingsSelected,
    showErrorSomeBookingIdsAreInvalid,
} from '@modules/client/utils/bookingsBulkChange/errorModals';
import BookingBulkChangeModalContent from '@modules/client/views/Bookings/BookingBulkChangeModalContent';
import {getBookingFormParams} from '@utils/booking';
import ClientBookingGeoRestrictionsGeneralInformationEditModel
    from '@modules/client/forms/ClientBookingGeoRestrictions/ClientBookingGeoRestrictionsGeneralInformationEditModel';
import ClientBookingGeoRestrictionsGeneralInformationForm
    from '@modules/client/forms/ClientBookingGeoRestrictions/ClientBookingGeoRestrictionsGeneralInformationForm';
import {EventBookingGeoRestrictionsStep} from '@modules/events/views/EventBookingGeoRestrictionsStep';
import {StepsContent} from '@appComponents/StepsContent';
import EventBookingGeoRestrictionsBlackoutZonesForm
    from '@modules/events/forms/EventBookingGeoRestrictions/EventBookingGeoRestrictionsBlackoutZonesForm';
import EventBookingGeoRestrictionsBlackoutZonesEditModel
    from '@modules/events/forms/EventBookingGeoRestrictions/EventBookingGeoRestrictionsBlackoutZonesEditModel';
import {hideModal, showModalConfirmation} from '@utils/modal';
import { CANCEL_BULK_CLIENT_BOOKINGS } from '@constants/messages';
import {bulkChangeClientBookings} from '@graphql/clients/mutation';

import client from '../../../apolloClient';

const parseAndValidateBookingsIds = (bookingsIds, showFormatError, isBulkCancel = false) => {
    if (!digitsConnectedByPlus(bookingsIds)) {
        return showFormatError();
    }

    const ids = bookingsIds.split('+').map((id) => convertToInt(id));

    if (0 === ids.length) {
        return showErrorNoBookingsSelected(isBulkCancel);
    }

    return ids;
};

export const createBookingForTable = (booking, geoRestrictions = [], hasMultipleGeoRestrictions = false) => {
    const isStageEvent = Boolean(_get(booking, 'st_event.id') || _get(booking, 'st_event.stage.id'));
    const hasStageType = Boolean(_get(booking, 'st_event.stage.stage_type.id', false));

    return {
        id: booking.booking_id,
        booking_id: booking.booking_id,
        event_id: booking.event_id,
        event_description: booking.event_description,
        event_external_description: booking.event_external_description,
        event_color: booking.event_color,
        match_or_stage_id: getMatchOrStageId(booking.match_id, booking.st_event?.stage?.id),
        match_id: booking.match_id,
        stage_id: _get(booking, 'st_event.stage.id', null),
        stage_name_or_description: getStagesDescription(booking),
        is_stage_event: isStageEvent,
        stage_sport_name: getCategoryLevel(booking, 1),
        stage_1st_category_name: getCategoryLevel(booking, 2),
        stage_2nd_category_name: getCategoryLevel(booking, 3),
        stage_name_with_tooltip: getStageEventNameWithTooltip(booking),
        event_content_id: booking.event_content_id,
        distribution_id: booking.distribution_id,
        start_datetime: booking.start_datetime,
        end_datetime: booking.end_datetime,
        match_sport_name: booking.match_sport_name,
        match_tournament_category_name: booking.match_tournament_category_name,
        match_tournament_name: booking.match_tournament_name,
        sr_event_sport_name: booking.sr_event_sport_name,
        sr_event_tournament_category_name: booking.sr_event_tournament_category_name,
        sr_event_tournament_name: booking.sr_event_tournament_name,
        content_category_level_1_name: booking.content_category_level_1_name,
        content_category_level_2_name: booking.content_category_level_2_name,
        content_category_level_3_name: booking.content_category_level_3_name,
        event_type: booking.event_type,
        content_tier_name: getContentTierName(booking),
        competitors: isStageEvent || hasStageType ? 0 : getCompetitors(booking),
        event_content_name: booking.event_content_name,
        event_content_type_name: booking.event_content_type_name,
        event_content_variant_id: booking.event_content_variant_id,
        event_content_variant_name: booking.event_content_variant_name,
        event_content_variant_short_name: booking.event_content_variant_short_name,
        client_package_id: booking.client_package_id,
        is_hq: booking.is_hq,
        is_ll: booking.is_ll,
        client_package_name: booking.client_package_name,
        product_short_name: booking.product_short_name,
        product_is_invoiceable: booking.product_is_invoiceable,
        booking_type_name: booking.booking_type_name,
        distribution_type_name: booking.distribution_type_name,
        price: booking.price,
        booking_creation_datetime: booking.booking_creation_datetime,
        event_invoice_status_name: booking.event_invoice_status_name,
        booking_invoice_status_name: booking.booking_invoice_status_name,
        booking_geo_restrictions: geoRestrictions,
        is_cancelled: booking.is_cancelled,
        has_multiple_geo_restrictions: hasMultipleGeoRestrictions,
        booked_by: booking.booked_by,
        invoice_status: getInvoiceStatus(booking),
        cancellation_user_name: booking.cancellation_user_name,
    };
};

export const getMatchOrStageId = (matchId, stageId) => matchId ?? stageId ?? null;

const updateDropdownState = (dropdownData, key, parser = dropdownParser) => {
    const dropdownItem = dropdownData[key] ?? [];

    return {[key]: parser(dropdownItem)};
};

const updatePropertyLicencesState = (dropdownData) => {
    const propertyLicences = dropdownData.propertyLicences ?? [];

    return {
        propertyLicencesOriginal: propertyLicences,
        propertyLicences: mapPropertyLicencesForDropdown(propertyLicences),
    };
};

export const updateClientBookingsState = (data) => {
    if (!data || !Array.isArray(data.clientBookings)) {
        return [];
    }

    return prepareGeoRestrictions(data.clientBookings);
};

const dropdownKeys = [
    'contentTiers',
    'bookingTypes',
    'clientPackages',
    'contentCategories',
    'deviceCategories',
    'distributionTypes',
    'eventTypes',
    'invoiceStatuses',
    'products',
    'sports',
    'eventContentTypes',
    'eventContentVariants',
    'contentCategoryLevel1',
];

export const updateDropdownValues = (dropdownData = {}) => {
    let newState = _reduce(dropdownKeys, (acc, key) => ({
        ...acc,
        ...updateDropdownState(dropdownData, key),
    }), {});

    return {
        ...newState,
        ...updateDropdownState(dropdownData, 'countries', (data) =>
            sortDropdownOptionsAlphanumerically(data)),
        ...updatePropertyLicencesState(dropdownData),
        ...updateDropdownState(dropdownData, 'sports', (data) =>
            sortDropdownOptionsAlphanumerically(dropdownParser(data))),
        ...updateDropdownState(dropdownData, 'contentCategoryLevel1', (data) =>
            sortDropdownOptionsAlphanumerically(dropdownParser(data))),
    };
};

export const prepareGeoRestrictions = (clientBookings) => {
    let resultBookings = [];

    _forEach(clientBookings, (booking) => {
        if (1 === booking.booking_geo_restrictions.length
            && null !== booking.booking_geo_restrictions[0].country_iso_alpha2_codes
        ) {
            resultBookings.push(createBookingForTable(
                booking,
                [booking.booking_geo_restrictions[0]]
            ));
        } else if (0 === booking.booking_geo_restrictions.length
            || null === booking.booking_geo_restrictions[0].country_iso_alpha2_codes
        ) {
            resultBookings.push(createBookingForTable(booking));
        } else {
            const allCountries = booking.booking_geo_restrictions
                    .map(el => el.country_iso_alpha2_codes)
                    .reduce((prev, curr) => (prev.concat(curr))).sort(),
                allBlackoutZones = booking.booking_geo_restrictions
                    .flatMap(el => el.blackout_zones)
                    .map(bz => bz.id),
                uniqueCountries = _uniq(allCountries),
                uniqueBlackoutZones = _uniq(allBlackoutZones),
                deviceCategoriesCount = booking.booking_geo_restrictions.length;

            if (
                uniqueCountries.length === (allCountries.length / deviceCategoriesCount)
                && uniqueBlackoutZones.length === (allBlackoutZones.length / deviceCategoriesCount)
            ) {
                const deviceCategoryName = booking.booking_geo_restrictions.map(
                    (geoRestriction) => _get(geoRestriction, 'device_category.name')
                ).join(', ');

                let geoRestriction = {
                    ...booking.booking_geo_restrictions[0],
                    device_category: {name: deviceCategoryName},
                };

                if (null !== booking.booking_geo_restrictions[0].country_iso_alpha2_codes) {
                    resultBookings.push(createBookingForTable(
                        booking,
                        [geoRestriction]
                    ));
                } else {
                    resultBookings.push(createBookingForTable(booking));
                }
            } else {
                let geoRestrictions = [];

                _forEach(booking.booking_geo_restrictions, (geoRestriction) => {
                    geoRestrictions.push(geoRestriction);
                });

                resultBookings.push(createBookingForTable(
                    booking,
                    geoRestrictions,
                    true
                ));
            }
        }
    });

    return resultBookings;
};

export const isGatewayTimeoutError = (error) => (
    responseStatuses.GATEWAY_TIMEOUT === error?.networkError?.statusCode
);

export const handleBulkBookingsError = (err) => {
    if (isGatewayTimeoutError(err)) {
        showPopupMessage(
            'Operation took too long. Wait a minute and refresh to confirm if operation succeeded.',
            'info',
            true
        );
    } else {
        showPopupMessage(
            'Bookings could not be cancelled.',
            'error'
        );
    }
};

export const handleClientError = (err, entityName) => {
    if (isGatewayTimeoutError(err)) {
        showPopupMessage(
            'Operation took too long. Please use filters to narrow your search.',
            'error',
            true
        );

        return;
    }

    showPopupMessage(
        `An error occurred while fetching ${entityName}.`,
        'error',
        true,
        err?.message
    );
};

export const useClientBookingsRoutes = (clientBookings, refetchClientBookings) => {
    const match = useRouteMatch();
    const location = useLocation();
    const {id, bookingId, bookingsIds} = useParams();

    return [
        {
            path: [
                routes.clients.bookings.edit.path,
                routes.clients.bookings.editGeoRestrictions.path,
                routes.clients.bookings.blackoutZones.path,
            ],
            render: () => {
                if (!isUrlParamValid(bookingId)) {
                    return renderModalError('Booking', getLink('clients.bookings.index', {id}));
                }

                const Forms = getBookingEditForms(match.params);

                return <StepsContent
                    title={'Edit booking'}
                    location={location}
                    tabs={Forms.tabs}
                    forms={Forms}
                />;
            },
            size: 'fullscreen',
        },
        {
            path: routes.clients.bookings.bulkChange.path,
            render: () => {
                const ids = parseAndValidateBookingsIds(
                    bookingsIds,
                    showErrorDuringBulkChange
                );

                return ids && <BookingBulkChangeModalContent
                    clientId={convertToInt(id)}
                    bookingsIds={ids}
                />;
            },
            size: 'fullscreen',
        },
        {
            path: routes.clients.bookings.bulkCancel.path,
            render: () => {
                const ids = parseAndValidateBookingsIds(
                    bookingsIds,
                    showErrorDuringBulkCancel,
                    true
                );

                ids && showBulkCancelModal(ids, clientBookings, refetchClientBookings);
            },
        },
    ];
};

const getBookingEditForms = (params) => {
    const [formParamsObject, linkParams] = getBookingFormParams(params.id, params.bookingId, params);

    return {
        tabs: [
            {
                key: 'step1',
                menuItem: {content: 'General information', disabled: false},
                pane: {
                    content: createForm(
                        ClientBookingGeoRestrictionsGeneralInformationEditModel,
                        ClientBookingGeoRestrictionsGeneralInformationForm,
                        formParamsObject
                    ),
                },
                url: getLink('clients.bookings.edit', linkParams),
            },
            {
                key: 'step2',
                menuItem: {content: 'Geo restrictions', disabled: false},
                pane: {
                    content: <EventBookingGeoRestrictionsStep
                        bookingId={convertToInt(params.bookingId)}
                    />,
                },
                url: getLink('clients.bookings.editGeoRestrictions', linkParams),
            },
            {
                key: 'step3',
                menuItem: {content: 'Blackout Zones', disabled: false},
                pane: {
                    content: createForm(
                        EventBookingGeoRestrictionsBlackoutZonesEditModel,
                        EventBookingGeoRestrictionsBlackoutZonesForm,
                        formParamsObject
                    ),
                },
                url: getLink('clients.bookings.blackoutZones', linkParams),
            },
        ],
    };
};

const showBulkCancelModal = (selectedBookingsIds, clientBookings, refetchClientBookings) => {
    const allClientBookingsIds = clientBookings.map(booking => convertToInt(booking.booking_id));

    if (!selectedBookingsIds.every(id => allClientBookingsIds.includes(id))) {
        return showErrorSomeBookingIdsAreInvalid();
    }

    showModalConfirmation({
        header: <Header content='Cancel bookings'/>,
        text: CANCEL_BULK_CLIENT_BOOKINGS(selectedBookingsIds.length),
        size: 'tiny',
        onYes: () => {
            client.mutate({
                mutation: bulkChangeClientBookings,
                variables: {
                    bookings: selectedBookingsIds,
                },
            }).then(() => {
                showPopupMessage(
                    'The selected bookings have been cancelled successfully.',
                    'success'
                );
                refetchClientBookings();
            }).catch((err) => {
                handleBulkBookingsError(err);
            }).finally(()=> hideModal());
        },
    });
};
